import { AlertTriangle, Loader2 } from 'lucide-react';

import type { ActiveRun } from '../lib/activeRuns';

/**
 * Avancement d'une génération, en une ligne.
 *
 * Posé dans la liste des avant-ventes plutôt que dans la seule fenêtre des
 * propositions : la génération se poursuit côté serveur quand cette fenêtre se
 * ferme, et rien ne le disait. On voit désormais d'un coup d'œil, depuis la
 * liste, quelles avant-ventes ont une proposition en préparation.
 *
 * Le temps écoulé est affiché avec l'étape. L'écriture du plan dure plusieurs
 * minutes et n'a rien d'anormal, mais une barre qui tourne sans rien dire ne
 * permet pas de distinguer une étape longue d'une exécution morte : c'est ce qui
 * est arrivé après un redéploiement fait pendant une génération.
 */

/** Durée écoulée, dite comme on la dirait à l'oral. */
function duree(secondes: number): string {
    if (secondes < 60) return `${secondes} s`;
    const minutes = Math.floor(secondes / 60);
    if (minutes < 60) return `${minutes} min`;
    const heures = Math.floor(minutes / 60);
    return `${heures} h ${String(minutes % 60).padStart(2, '0')}`;
}

export function GenerationProgress({ run, compact }: { run: ActiveRun; compact?: boolean }) {
    const pourcent = Math.round(Math.min(1, Math.max(0, run.progress)) * 100);
    const ecoule = duree(run.elapsedSeconds || 0);

    return (
        <div
            className={compact ? 'w-32' : 'w-full'}
            title={`${run.label} — ${pourcent} %, ${ecoule}`}
        >
            <div className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
                {run.stalled
                    ? <AlertTriangle className="h-3 w-3 shrink-0 text-[var(--color-warning)]" />
                    : <Loader2 className="h-3 w-3 shrink-0 animate-spin text-[var(--color-primary)]" />}
                <span className="truncate">
                    {run.stalled ? 'Interrompue, reprise en cours' : run.label}
                </span>
                {!compact && (
                    <span className="shrink-0 text-[var(--color-text-muted)]">{ecoule}</span>
                )}
            </div>
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-[var(--color-bg-hover)]">
                <div
                    className={`h-full rounded-full transition-all duration-700 ${
                        run.stalled ? 'bg-[var(--color-warning)]' : 'bg-[var(--color-primary)]'
                    }`}
                    style={{ width: `${Math.max(4, pourcent)}%` }}
                />
            </div>
        </div>
    );
}
